// src/Pages/SettingsPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { turso } from '@/database/client';
import { downloadUserData, exportUserData, createLocalBackup } from '@/lib/dataExport';
import { changePassword } from '@/lib/passwordReset';
import { validatePasswordStrength } from '@/lib/auth';
import NotificationSettings from '@/components/NotificationSettings';
import { 
  ArrowLeft, 
  User, 
  Bell, 
  Shield, 
  Trash2, 
  Eye, 
  EyeOff,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, signOut, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'notifications' | 'privacy' | 'account'>('profile');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Change password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Profile settings
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar || '');
  const [profileColor, setProfileColor] = useState(user?.profile_color || 'bg-blue-500');
  const [timezone, setTimezone] = useState(
    localStorage.getItem('whipt_timezone') || Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  // Notification settings
  const [emailNotifications, setEmailNotifications] = useState(
    localStorage.getItem('whipt_email_notifications') !== 'false'
  );
  const [browserNotifications, setBrowserNotifications] = useState(
    localStorage.getItem('whipt_browser_notifications') !== 'false'
  );
  const [messageNotifications, setMessageNotifications] = useState(
    localStorage.getItem('whipt_message_notifications') !== 'false'
  );

  // Privacy settings
  const [onlineStatus, setOnlineStatus] = useState(
    localStorage.getItem('whipt_online_status') !== 'false'
  );
  const [showAds, setShowAds] = useState(
    localStorage.getItem('whipt_ads_enabled') !== 'false'
  );
  const [forceStudentDefaults, setForceStudentDefaults] = useState(
    localStorage.getItem('whipt_force_student_defaults') === 'true'
  );

  // Load notification preferences from database
  useEffect(() => {
    const loadPreferences = async () => {
      if (user) {
        try {
          // Load user profile from users table (name, email)
          const userResult = await turso.execute({
            sql: "SELECT name, email FROM users WHERE id = ? LIMIT 1",
            args: [user.id]
          });
          
          if (userResult.rows && userResult.rows.length > 0) {
            const userData = userResult.rows[0];
            setName(userData.name as string);
            setEmail(userData.email as string);
            console.log('✅ Loaded user profile from database');
          }
          
          // Load notification preferences
          const notifResult = await turso.execute({
            sql: "SELECT data_value FROM user_data WHERE user_id = ? AND data_type = 'preferences' AND data_key = 'notifications' LIMIT 1",
            args: [user.id]
          });
          
          if (notifResult.rows && notifResult.rows.length > 0) {
            const prefs = JSON.parse(notifResult.rows[0].data_value as string);
            setEmailNotifications(prefs.email ?? true);
            setBrowserNotifications(prefs.browser ?? true);
            setMessageNotifications(prefs.messages ?? true);
            console.log('✅ Loaded notification preferences from database');
          }
          
          // Load privacy preferences
          const privacyResult = await turso.execute({
            sql: "SELECT data_value FROM user_data WHERE user_id = ? AND data_type = 'preferences' AND data_key = 'privacy' LIMIT 1",
            args: [user.id]
          });
          
          if (privacyResult.rows && privacyResult.rows.length > 0) {
            const prefs = JSON.parse(privacyResult.rows[0].data_value as string);
            setOnlineStatus(prefs.onlineStatus ?? true);
            setShowAds(prefs.showAds ?? true);
            setForceStudentDefaults(prefs.forceStudentDefaults ?? false);
            console.log('✅ Loaded privacy preferences from database');
          }
          
          // Load profile preferences (avatar and profile color)
          const profileResult = await turso.execute({
            sql: "SELECT data_value FROM user_data WHERE user_id = ? AND data_type = 'preferences' AND data_key = 'profile' LIMIT 1",
            args: [user.id]
          });
          
          if (profileResult.rows && profileResult.rows.length > 0) {
            const prefs = JSON.parse(profileResult.rows[0].data_value as string);
            if (prefs.avatar) setAvatarUrl(prefs.avatar);
            if (prefs.profileColor) setProfileColor(prefs.profileColor);
            console.log('✅ Loaded profile preferences from database');
          }
        } catch (error) {
          console.error('Failed to load preferences:', error);
        }
      }
    };
    
    loadPreferences();
  }, [user]);

  const handleSaveProfile = async () => {
    if (user) {
      try {
        // Update in database
        await turso.execute({
          sql: 'UPDATE users SET name = ?, email = ? WHERE id = ?',
          args: [name, email, user.id]
        });
        
        // Save avatar and profile color to user_data
        const dataId = `userdata_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const profileData = {
          avatar: avatarUrl,
          profileColor: profileColor
        };
        
        // Delete old profile data
        await turso.execute({
          sql: "DELETE FROM user_data WHERE user_id = ? AND data_type = 'preferences' AND data_key = 'profile'",
          args: [user.id]
        });
        
        // Insert new profile data
        await turso.execute({
          sql: `INSERT INTO user_data (id, user_id, data_type, data_key, data_value, created_at, updated_at)
                VALUES (?, ?, 'preferences', 'profile', ?, datetime('now'), datetime('now'))`,
          args: [dataId, user.id, JSON.stringify(profileData)]
        });
        
        // Update localStorage
        const updatedUser = { ...user, name, email, avatar: avatarUrl, profile_color: profileColor };
        localStorage.setItem('whipt_user', JSON.stringify(updatedUser));
        
        // Refresh the user in AuthContext
        await refreshUser();
        
        console.log('✅ Profile updated in database');
        setSuccessMessage('Profile updated successfully!');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } catch (error) {
        console.error('Failed to update profile:', error);
        alert('Failed to update profile. Please try again.');
      }
    }
  };

  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Add timestamp to bust cache
        const avatarWithTimestamp = reader.result as string;
        setAvatarUrl(avatarWithTimestamp);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveAvatar = () => {
    setAvatarUrl('');
  };

  const handleSaveNotifications = async () => {
    // Request browser notification permission if enabling
    if (browserNotifications && 'Notification' in window) {
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (permission === 'denied') {
          alert('Browser notifications are blocked. Please enable them in your browser settings.');
          setBrowserNotifications(false);
          return;
        }
      }
    }
    
    // Save to localStorage
    localStorage.setItem('whipt_email_notifications', String(emailNotifications));
    localStorage.setItem('whipt_browser_notifications', String(browserNotifications));
    localStorage.setItem('whipt_message_notifications', String(messageNotifications));
    
    // Save to database
    if (user) {
      try {
        const dataId = `userdata_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const preferences = {
          email: emailNotifications,
          browser: browserNotifications,
          messages: messageNotifications
        };
        
        // Delete old notification preferences
        await turso.execute({
          sql: "DELETE FROM user_data WHERE user_id = ? AND data_type = 'preferences' AND data_key = 'notifications'",
          args: [user.id]
        });
        
        // Insert new preferences
        await turso.execute({
          sql: `INSERT INTO user_data (id, user_id, data_type, data_key, data_value, created_at, updated_at)
                VALUES (?, ?, 'preferences', 'notifications', ?, datetime('now'), datetime('now'))`,
          args: [dataId, user.id, JSON.stringify(preferences)]
        });
        
        console.log('✅ Notification preferences saved to database');
      } catch (error) {
        console.error('Failed to save notification preferences to database:', error);
      }
    }
    
    setSuccessMessage('Notification preferences saved!');
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const handleSavePrivacy = async () => {
    // Save to localStorage
    localStorage.setItem('whipt_online_status', String(onlineStatus));
    localStorage.setItem('whipt_ads_enabled', String(showAds));
    localStorage.setItem('whipt_force_student_defaults', String(forceStudentDefaults));
    
    // Save to database
    if (user) {
      try {
        const dataId = `userdata_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const preferences = {
          onlineStatus: onlineStatus,
          showAds: showAds,
          forceStudentDefaults,
        };
        
        // Delete old privacy preferences
        await turso.execute({
          sql: "DELETE FROM user_data WHERE user_id = ? AND data_type = 'preferences' AND data_key = 'privacy'",
          args: [user.id]
        });
        
        // Insert new preferences
        await turso.execute({
          sql: `INSERT INTO user_data (id, user_id, data_type, data_key, data_value, created_at, updated_at)
                VALUES (?, ?, 'preferences', 'privacy', ?, datetime('now'), datetime('now'))`,
          args: [dataId, user.id, JSON.stringify(preferences)]
        });
        
        console.log('✅ Privacy preferences saved to database');
      } catch (error) {
        console.error('Failed to save privacy preferences to database:', error);
      }
    }
    
    setSuccessMessage('Privacy settings saved!');
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const handleExportData = async () => {
    if (!user) return;
    
    setIsExporting(true);
    setExportError(null);
    
    try {
      // Export and download data
      await downloadUserData(user.id);
      
      // Also create local backup
      const data = await exportUserData(user.id);
      createLocalBackup(data);
      
      setSuccessMessage('Data exported successfully!');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      
      console.log('✅ User data exported');
    } catch (error) {
      console.error('Failed to export data:', error);
      setExportError('Failed to export data. Please try again.');
      setTimeout(() => setExportError(null), 5000);
    } finally {
      setIsExporting(false);
    }
  };

  const handleChangePassword = async () => {
    if (!user) return;
    
    setPasswordError(null);
    
    // Validate inputs
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (currentPassword === newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }
    
    // Validate password strength
    const validation = validatePasswordStrength(newPassword);
    if (!validation.isValid) {
      setPasswordError(validation.errors.join('\n'));
      return;
    }
    
    setIsChangingPassword(true);
    
    try {
      const result = await changePassword(user.id, currentPassword, newPassword);
      
      if (result.success) {
        setSuccessMessage('Password changed successfully!');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
        
        // Clear form
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPasswordError(result.error || 'Failed to change password');
      }
    } catch (error) {
      console.error('Failed to change password:', error);
      setPasswordError('An error occurred. Please try again.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDeleteAccount = () => {
    if (deleteConfirmText.toLowerCase() !== 'delete my account') {
      alert('Please type "delete my account" to confirm');
      return;
    }

    // Clear all user data
    const keysToRemove = Object.keys(localStorage).filter(key => key.startsWith('whipt_'));
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Logout and redirect
    signOut();
    navigate('/organizer/landing');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#05020b] via-[#0a0513] to-[#0f0820] text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 shadow-lg">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/organizer')}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">Settings</h1>
              <p className="text-sm text-amber-100">Manage your account and preferences</p>
            </div>
          </div>
                      <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-semibold text-white">{user?.name}</p>
              <p className="text-xs text-amber-100">{user?.email}</p>
            </div>
            <div className="h-8 w-px bg-white/20 mx-2" />
            <a
              href="mailto:support@theblackbettermentquest.org"
              className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-semibold text-white transition-colors flex items-center gap-2"
              title="Contact Support"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Support
            </a>
            {/* Admin button removed - use direct URL /admin if needed */}
          </div>
        </div>
      </div>      {/* Success Message */}
      {showSuccess && (
        <div className="fixed top-20 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-xl shadow-2xl flex items-center gap-2 animate-slide-in">
          <CheckCircle className="w-5 h-5" />
          <span className="font-semibold">{successMessage}</span>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-slate-900/50 border border-slate-700 rounded-2xl p-4 space-y-2">
              <button
                onClick={() => setActiveTab('profile')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  activeTab === 'profile'
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg'
                    : 'text-slate-300 hover:bg-slate-800'
                }`}
              >
                <User className="w-5 h-5" />
                <span className="font-semibold">Profile</span>
              </button>
              <button
                onClick={() => setActiveTab('notifications')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  activeTab === 'notifications'
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg'
                    : 'text-slate-300 hover:bg-slate-800'
                }`}
              >
                <Bell className="w-5 h-5" />
                <span className="font-semibold">Notifications</span>
              </button>
              <button
                onClick={() => setActiveTab('privacy')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  activeTab === 'privacy'
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg'
                    : 'text-slate-300 hover:bg-slate-800'
                }`}
              >
                <Shield className="w-5 h-5" />
                <span className="font-semibold">Privacy</span>
              </button>
              <button
                onClick={() => setActiveTab('account')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  activeTab === 'account'
                    ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg'
                    : 'text-slate-300 hover:bg-slate-800'
                }`}
              >
                <Trash2 className="w-5 h-5" />
                <span className="font-semibold">Account</span>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="bg-slate-900/50 border border-slate-700 rounded-2xl p-6">
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Profile Information</h2>
                    <p className="text-slate-400">Update your personal details</p>
                  </div>

                  <div className="space-y-4">
                    {/* Avatar Upload Section */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-300 mb-3">
                        Profile Avatar
                      </label>
                      <div className="flex items-center gap-6">
                        {avatarUrl ? (
                          <img 
                            src={avatarUrl} 
                            alt="Profile avatar"
                            className="w-24 h-24 rounded-full object-cover border-4 border-amber-500 shadow-lg"
                          />
                        ) : (
                          <div 
                            className={`w-24 h-24 rounded-full ${profileColor} flex items-center justify-center text-white font-bold text-3xl border-4 border-amber-500 shadow-lg`}
                          >
                            {name?.substring(0, 2).toUpperCase() || 'U'}
                          </div>
                        )}
                        <div className="flex-1 space-y-3">
                          <div className="flex gap-3">
                            <label className="cursor-pointer px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-semibold transition-colors">
                              Upload Image
                              <input 
                                type="file" 
                                accept="image/*"
                                onChange={handleAvatarUpload}
                                className="hidden"
                              />
                            </label>
                            {avatarUrl && (
                              <button
                                onClick={handleRemoveAvatar}
                                className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white font-semibold transition-colors"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          <p className="text-xs text-slate-400">
                            Upload a JPG, PNG, or GIF. Max size 5MB.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Avatar Color Picker */}
                    {!avatarUrl && (
                      <div>
                        <label className="block text-sm font-semibold text-slate-300 mb-3">
                          Avatar Background Color
                        </label>
                        <div className="flex flex-wrap gap-3">
                          {['bg-blue-500', 'bg-purple-500', 'bg-pink-500', 'bg-red-500', 'bg-orange-500', 'bg-amber-500', 'bg-green-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-violet-500'].map((color) => (
                            <button
                              key={color}
                              onClick={() => setProfileColor(color)}
                              className={`w-12 h-12 rounded-full ${color} border-4 ${profileColor === color ? 'border-white scale-110' : 'border-transparent'} transition-all hover:scale-105`}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-semibold text-slate-300 mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-300 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-300 mb-2">
                        Timezone
                      </label>
                      <select
                        value={timezone}
                        onChange={(e) => {
                          setTimezone(e.target.value);
                          localStorage.setItem('whipt_timezone', e.target.value);
                        }}
                        className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="America/New_York">Eastern Time (ET)</option>
                        <option value="America/Chicago">Central Time (CT)</option>
                        <option value="America/Denver">Mountain Time (MT)</option>
                        <option value="America/Los_Angeles">Pacific Time (PT)</option>
                        <option value="America/Anchorage">Alaska Time (AKT)</option>
                        <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
                        <option value="Europe/London">London (GMT)</option>
                        <option value="Europe/Paris">Paris (CET)</option>
                        <option value="Asia/Tokyo">Tokyo (JST)</option>
                        <option value="Asia/Shanghai">Shanghai (CST)</option>
                        <option value="Australia/Sydney">Sydney (AEDT)</option>
                      </select>
                      <p className="text-xs text-slate-400 mt-2">
                        All dates and times will be displayed in your selected timezone
                      </p>
                    </div>

                    <button
                      onClick={handleSaveProfile}
                      className="px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:shadow-lg transition-all"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Notification Preferences</h2>
                    <p className="text-slate-400">Choose how you want to be notified</p>
                  </div>

                  {/* Advanced Notification Settings Link */}
                  <button
                    onClick={() => navigate('/organizer/settings/notifications')}
                    className="w-full flex items-center justify-between p-6 rounded-xl bg-gradient-to-r from-purple-600/20 to-indigo-600/20 border-2 border-purple-500/40 hover:border-purple-500/60 transition-all group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-purple-500/20 rounded-xl group-hover:bg-purple-500/30 transition-colors">
                        <Bell className="w-6 h-6 text-purple-300" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-white text-lg">Advanced Notification Settings</p>
                        <p className="text-sm text-purple-200/80">Configure calendar reminders, deadlines, quiet hours & more</p>
                      </div>
                    </div>
                    <svg className="w-6 h-6 text-purple-300 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  {/* Desktop Notifications Component */}
                  <NotificationSettings />

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                      <div>
                        <p className="font-semibold text-white">Email Notifications</p>
                        <p className="text-sm text-slate-400">Receive updates via email</p>
                      </div>
                      <button
                        onClick={() => setEmailNotifications(!emailNotifications)}
                        className={`relative w-14 h-8 rounded-full transition-colors ${
                          emailNotifications ? 'bg-amber-500' : 'bg-slate-700'
                        }`}
                      >
                        <div
                          className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform ${
                            emailNotifications ? 'translate-x-7' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                      <div>
                        <p className="font-semibold text-white">Browser Notifications</p>
                        <p className="text-sm text-slate-400">Get desktop notifications</p>
                      </div>
                      <button
                        onClick={() => setBrowserNotifications(!browserNotifications)}
                        className={`relative w-14 h-8 rounded-full transition-colors ${
                          browserNotifications ? 'bg-amber-500' : 'bg-slate-700'
                        }`}
                      >
                        <div
                          className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform ${
                            browserNotifications ? 'translate-x-7' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                      <div>
                        <p className="font-semibold text-white">Message Notifications</p>
                        <p className="text-sm text-slate-400">New chat message alerts</p>
                      </div>
                      <button
                        onClick={() => setMessageNotifications(!messageNotifications)}
                        className={`relative w-14 h-8 rounded-full transition-colors ${
                          messageNotifications ? 'bg-amber-500' : 'bg-slate-700'
                        }`}
                      >
                        <div
                          className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform ${
                            messageNotifications ? 'translate-x-7' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    <button
                      onClick={handleSaveNotifications}
                      className="px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:shadow-lg transition-all"
                    >
                      Save Preferences
                    </button>
                  </div>
                </div>
              )}

              {/* Privacy Tab */}
              {activeTab === 'privacy' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Privacy Settings</h2>
                    <p className="text-slate-400">Control your privacy and visibility</p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                      <div>
                        <p className="font-semibold text-white">Show Online Status</p>
                        <p className="text-sm text-slate-400">Let others see when you're online</p>
                      </div>
                      <button
                        onClick={() => setOnlineStatus(!onlineStatus)}
                        className={`relative w-14 h-8 rounded-full transition-colors ${
                          onlineStatus ? 'bg-green-500' : 'bg-slate-700'
                        }`}
                      >
                        <div
                          className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform ${
                            onlineStatus ? 'translate-x-7' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                      <div>
                        <p className="font-semibold text-white">Show Advertisements</p>
                        <p className="text-sm text-slate-400">Display promotional content</p>
                      </div>
                      <button
                        onClick={() => setShowAds(!showAds)}
                        className={`relative w-14 h-8 rounded-full transition-colors ${
                          showAds ? 'bg-amber-500' : 'bg-slate-700'
                        }`}
                      >
                        <div
                          className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform ${
                            showAds ? 'translate-x-7' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                      <div>
                        <p className="font-semibold text-white">Force Student Organizer Layout</p>
                        <p className="text-sm text-slate-400">Keep the 4 student tabs and ignore mixed data (bypasses DB notebooks)</p>
                      </div>
                      <button
                        onClick={() => setForceStudentDefaults(!forceStudentDefaults)}
                        className={`relative w-14 h-8 rounded-full transition-colors ${
                          forceStudentDefaults ? 'bg-green-500' : 'bg-slate-700'
                        }`}
                      >
                        <div
                          className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform ${
                            forceStudentDefaults ? 'translate-x-7' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    <button
                      onClick={handleSavePrivacy}
                      className="px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:shadow-lg transition-all"
                    >
                      Save Settings
                    </button>
                  </div>
                </div>
              )}

              {/* Account Tab */}
              {activeTab === 'account' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Account Management</h2>
                    <p className="text-slate-400">Manage your account and data</p>
                  </div>

                  {/* Change Password Section */}
                  <div className="border-2 border-blue-500/30 rounded-xl p-6 bg-blue-500/5">
                    <div className="flex items-start gap-4 mb-4">
                      <svg className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-blue-400 mb-2">Change Password</h3>
                        <p className="text-slate-300 mb-4">
                          Update your password to keep your account secure
                        </p>
                        
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                              Current Password
                            </label>
                            <input
                              type="password"
                              value={currentPassword}
                              onChange={(e) => setCurrentPassword(e.target.value)}
                              className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-blue-500 focus:outline-none"
                              placeholder="Enter current password"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                              New Password
                            </label>
                            <input
                              type="password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-blue-500 focus:outline-none"
                              placeholder="Enter new password"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                              Confirm New Password
                            </label>
                            <input
                              type="password"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-blue-500 focus:outline-none"
                              placeholder="Confirm new password"
                            />
                          </div>
                          
                          <div className="text-xs text-slate-400 space-y-1">
                            <p className="font-semibold">Password requirements:</p>
                            <ul className="list-disc list-inside space-y-1">
                              <li>At least 8 characters</li>
                              <li>One uppercase letter (A-Z)</li>
                              <li>One lowercase letter (a-z)</li>
                              <li>One number (0-9)</li>
                              <li>One special character (!@#$%^&*...)</li>
                            </ul>
                          </div>
                          
                          {passwordError && (
                            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm whitespace-pre-line">
                              {passwordError}
                            </div>
                          )}
                          
                          <button
                            onClick={handleChangePassword}
                            disabled={isChangingPassword}
                            className="px-6 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isChangingPassword ? (
                              <>
                                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Changing Password...</span>
                              </>
                            ) : (
                              <>
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                </svg>
                                <span>Change Password</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Logout Section */}
                  <div className="border-2 border-amber-500/30 rounded-xl p-6 bg-amber-500/5">
                    <div className="flex items-start gap-4 mb-4">
                      <svg className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-amber-400 mb-2">Sign Out</h3>
                        <p className="text-slate-300 mb-4">
                          Sign out of your WHIPT account. Your data will be saved and available when you sign back in.
                        </p>
                        <button
                          onClick={() => {
                            signOut();
                            navigate('/organizer/landing');
                          }}
                          className="px-6 py-3 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white font-semibold transition-all flex items-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Data Export Section */}
                  <div className="border-2 border-green-500/30 rounded-xl p-6 bg-green-500/5">
                    <div className="flex items-start gap-4 mb-4">
                      <svg className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-green-400 mb-2">Export Your Data</h3>
                        <p className="text-slate-300 mb-4">
                          Download all your data as a JSON file. This includes:
                        </p>
                        <ul className="list-disc list-inside space-y-1 text-slate-400 text-sm mb-4">
                          <li>Calendar events and assignments</li>
                          <li>User preferences and settings</li>
                          <li>Account information (excluding password)</li>
                        </ul>
                        <p className="text-slate-400 text-sm mb-4">
                          Your data will be downloaded as a JSON file that you can keep as a backup or import into another account.
                        </p>
                        {exportError && (
                          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                            <span>{exportError}</span>
                          </div>
                        )}
                        <button
                          onClick={handleExportData}
                          disabled={isExporting}
                          className="px-6 py-3 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-semibold transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isExporting ? (
                            <>
                              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span>Exporting...</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              <span>Export Data</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h2 className="text-2xl font-bold text-red-400 mb-2">Danger Zone</h2>
                    <p className="text-slate-400">Permanent account actions</p>
                  </div>

                  {/* Contact Support Section */}
                  <div className="border-2 border-blue-500/30 rounded-xl p-6 bg-blue-500/5">
                    <h3 className="text-xl font-bold text-blue-400 mb-3">Need Help?</h3>
                    <p className="text-slate-300 mb-4">
                      Before deleting your account, reach out to our support team. We're here to help!
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <svg className="w-5 h-5 text-blue-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <div>
                          <p className="text-sm font-semibold text-white">General Inquiries</p>
                          <a href="mailto:contact@theblackbettermentquest.org" className="text-sm text-blue-400 hover:text-blue-300 hover:underline">
                            contact@theblackbettermentquest.org
                          </a>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        <div>
                          <p className="text-sm font-semibold text-white">Technical Support</p>
                          <a href="mailto:support@theblackbettermentquest.org" className="text-sm text-green-400 hover:text-green-300 hover:underline">
                            support@theblackbettermentquest.org
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Legal & Support Section */}
                  <div className="border border-slate-700 rounded-xl p-6 bg-slate-800/50">
                    <h3 className="text-xl font-bold text-white mb-4">Legal & Support</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <a
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-4 rounded-lg bg-slate-700/50 hover:bg-slate-700 border border-slate-600 hover:border-purple-400/50 transition-all group"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-white group-hover:text-purple-300 transition-colors">Terms of Service</h4>
                            <p className="text-sm text-slate-400 mt-1">View our terms and community guidelines</p>
                          </div>
                          <svg className="w-5 h-5 text-slate-400 group-hover:text-purple-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </div>
                      </a>
                      
                      <a
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-4 rounded-lg bg-slate-700/50 hover:bg-slate-700 border border-slate-600 hover:border-purple-400/50 transition-all group"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-white group-hover:text-purple-300 transition-colors">Privacy Policy</h4>
                            <p className="text-sm text-slate-400 mt-1">How we protect your data</p>
                          </div>
                          <svg className="w-5 h-5 text-slate-400 group-hover:text-purple-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </div>
                      </a>
                      
                      <a
                        href="mailto:support@theblackbettermentquest.org"
                        className="p-4 rounded-lg bg-slate-700/50 hover:bg-slate-700 border border-slate-600 hover:border-purple-400/50 transition-all group"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-white group-hover:text-purple-300 transition-colors">Contact Support</h4>
                            <p className="text-sm text-slate-400 mt-1">Get help with your account</p>
                          </div>
                          <svg className="w-5 h-5 text-slate-400 group-hover:text-purple-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                      </a>
                      
                      <a
                        href="mailto:contact@theblackbettermentquest.org"
                        className="p-4 rounded-lg bg-slate-700/50 hover:bg-slate-700 border border-slate-600 hover:border-purple-400/50 transition-all group"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-white group-hover:text-purple-300 transition-colors">General Inquiries</h4>
                            <p className="text-sm text-slate-400 mt-1">Questions or feedback</p>
                          </div>
                          <svg className="w-5 h-5 text-slate-400 group-hover:text-purple-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                      </a>
                    </div>
                  </div>

                  <div className="border-2 border-red-500/30 rounded-xl p-6 bg-red-500/5">
                    <div className="flex items-start gap-4 mb-4">
                      <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-xl font-bold text-red-400 mb-2">Delete Account</h3>
                        <p className="text-slate-300 mb-4">
                          Once you delete your account, there is no going back. This will permanently delete:
                        </p>
                        <ul className="list-disc list-inside space-y-1 text-slate-400 text-sm mb-4">
                          <li>Your profile and personal information</li>
                          <li>All class notebooks and assignments</li>
                          <li>Chat messages and conversations</li>
                          <li>Uploaded files and documents</li>
                          <li>Settings and preferences</li>
                        </ul>
                        <p className="text-red-300 font-semibold">
                          This action cannot be undone!
                        </p>
                      </div>
                    </div>

                    {!showDeleteConfirm ? (
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="px-6 py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold transition-all flex items-center gap-2"
                      >
                        <Trash2 className="w-5 h-5" />
                        Delete My Account
                      </button>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-slate-300 mb-2">
                            Type <span className="text-red-400">"delete my account"</span> to confirm
                          </label>
                          <input
                            type="text"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            placeholder="delete my account"
                            className="w-full px-4 py-3 rounded-xl bg-slate-800 border-2 border-red-500/50 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                          />
                        </div>
                        <div className="flex gap-3">
                          <button
                            onClick={() => {
                              setShowDeleteConfirm(false);
                              setDeleteConfirmText('');
                            }}
                            className="flex-1 px-6 py-3 rounded-xl bg-slate-700 hover:bg-slate-600 text-white font-semibold transition-all"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleDeleteAccount}
                            disabled={deleteConfirmText.toLowerCase() !== 'delete my account'}
                            className="flex-1 px-6 py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Permanently Delete Account
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
